import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useSocket } from "@/hooks/useSocket";
import ChatList from "@/components/chat/ChatList";
import ChatThread from "@/components/chat/ChatThread";
import NewChatDialog from "@/components/chat/NewChatDialog";
import SettingsDialog from "@/components/chat/SettingsDialog";
import AIDialog from "@/components/chat/AIDialog";
import AdminDialog from "@/components/chat/AdminDialog";
import CallOverlay from "@/components/chat/CallOverlay";
import { WifiOff } from "lucide-react";
import AndroidUpdateBanner from "@/components/AndroidUpdateBanner";
import IOSInstallPrompt from "@/components/IOSInstallPrompt";
import NotificationPrompt from "@/components/NotificationPrompt";
import InstallCelebration from "@/components/InstallCelebration";
import { idb } from "@/lib/idb";
import GroupCallOverlay from "@/components/chat/GroupCallOverlay";
import LiveScreen from "@/components/live/LiveScreen";
import { setLiveSessions } from "@/lib/liveHosts";
import BottomNav from "@/components/chat/BottomNav";
import SettingsPage from "@/components/chat/SettingsPage";
import SocialPage from "@/components/social/SocialPage";
import SocialConsentDialog from "@/components/social/SocialConsentDialog";
import MarketplacePage from "@/components/market/MarketplacePage";
import ProfileSheet from "@/components/chat/ProfileSheet";
import CallsPage from "@/components/chat/CallsPage";
import { playRecv, loadRingtones, syncSelectionFromUser } from "@/lib/sound";
import LockScreen from "@/components/chat/LockScreen";
import { hasLock, isUnlocked, lockNow } from "@/lib/appLock";
import { purgeOldMedia } from "@/lib/mediaCache";
import { mediaTTLDays } from "@/lib/prefs";

// Normalize a push-notification "opened" payload (Median bridge or web SW) into a route intent.
// The pressed action-button id ("accept"/"decline") arrives in different shapes depending on the
// Median/OneSignal build (SDK v5 vs legacy v4). Dig through every known nesting so we can reliably
// tell an Accept tap from a Decline tap; a plain body tap has no button id (OneSignal uses the
// sentinel "__DEFAULT__" or none) → treated as neither.
function extractAct(d) {
  if (!d) return null;
  const cands = [
    d.actionId, d.actionSelected, d.actionID,
    d.action && (typeof d.action === "string" ? d.action : d.action.actionId),
    d.action && d.action.id,
    d.result && d.result.action && (d.result.action.actionId || d.result.action.id),
    d.result && d.result.actionId,
    d.notification && d.notification.actionId,
    d.button && (d.button.id || d.button.actionId),
    d.buttonId,
  ];
  const ad = d.additionalData || d.additional_data || d.data || {};
  cands.push(ad.actionId, ad.action, ad.buttonId);
  for (const c of cands) if (typeof c === "string" && c) return c;
  return null;
}

const DECLINE_ACTS = ["decline", "reject", "dismiss", "hangup", "hang_up", "no", "end"];
const ACCEPT_ACTS = ["accept", "answer", "yes", "pickup", "pick_up"];

function parsePushData(d) {
  if (!d) return null;
  const ad = d.additionalData || d.additional_data || d.data || d;
  const raw = extractAct(d);
  const key = String(raw || "").toLowerCase();
  let act = raw;
  if (DECLINE_ACTS.includes(key)) act = "decline";
  else if (ACCEPT_ACTS.includes(key)) act = "accept";
  const video = ad.video === true || ad.video === "1" || ad.video === 1 || ad.video === "true";
  const type = ad.type || (ad.callerId ? "call" : (ad.chatId ? "message" : null));
  return { type, callerId: ad.callerId, callerName: ad.callerName, chatId: ad.chatId, video, act, actRaw: raw };
}

export default function ChatApp() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [socialJoinOpen, setSocialJoinOpen] = useState(false); // opt-in gate for the Social tab
  const [socialOptBusy, setSocialOptBusy] = useState(false);
  const [locked, setLocked] = useState(() => hasLock() && !isUnlocked());
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState({}); // chatId -> name
  const [dialog, setDialog] = useState(null); // new | settings | ai | admin
  const [online, setOnline] = useState(navigator.onLine);
  const [incomingCall, setIncomingCall] = useState(null);
  const [pendingPushCall, setPendingPushCall] = useState(null); // {callerId, decline?} queued push-accept handshake
  const [pendingCall, setPendingCall] = useState(null); // {chatId, video} for quick call-back
  const [groupCall, setGroupCall] = useState(null); // {chat, video, incoming?}
  const [tab, setTab] = useState("chats");
  // Marketplace is admin-only for now (hidden from the bottom nav + regular users).
  // Flip this to `true` for everyone when the marketplace is ready to launch.
  const canMarket = ["admin", "manager", "supervisor"].includes(user?.role);
  const [socialMsg, setSocialMsg] = useState(null); // latest social DM for live thread updates
  const [newPostSignal, setNewPostSignal] = useState(null); // live new-post pings for Discover badge
  const [marketMsg, setMarketMsg] = useState(null); // latest marketplace DM
  const [socialUnread, setSocialUnread] = useState(0);
  const [socialImmersive, setSocialImmersive] = useState(false);
  const [marketUnread, setMarketUnread] = useState(0);
  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(() => { syncSelectionFromUser(user); loadRingtones(true); }, [user?.id]);
  const seenDmRef = useRef(new Set()); // dedup incoming social/market DMs by id
  const [marketView, setMarketView] = useState("browse"); // initial Market sub-tab
  const [marketOpenId, setMarketOpenId] = useState(null); // deep-link/shared product to open
  const [socialOpenPostId, setSocialOpenPostId] = useState(null); // deep-link/shared post to open
  const [profileOpen, setProfileOpen] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const activeRef = useRef(null);
  activeRef.current = activeId;
  const incomingRef = useRef(null);
  incomingRef.current = incomingCall;
  const pushCallRef = useRef(null);
  const routeRef = useRef(null);

  // App Lock: re-lock when returning to the foreground after being hidden a while; purge old media
  useEffect(() => {
    purgeOldMedia(mediaTTLDays());
    let hiddenAt = 0;
    const onVis = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); }
      else if (hasLock() && hiddenAt && Date.now() - hiddenAt > 60000) { lockNow(); }
    };
    const onLock = () => setLocked(true);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("rc-lock", onLock);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("rc-lock", onLock); };
  }, []);

  // Handle channel invite deep-links: /app?join=CODE
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    let code = params.get("join");
    if (!code) {
      // Came through /join/{code} → sign-in/sign-up → here
      try {
        const after = sessionStorage.getItem("rc_after_auth") || "";
        if (after.includes("join=")) code = new URLSearchParams(after.split("?")[1]).get("join");
      } catch {}
    }
    if (!code) return;
    try { sessionStorage.removeItem("rc_after_auth"); } catch {}
    api.post("/channels/join-by-code", { code })
      .then(async ({ data }) => { await loadChats(); setActiveId(data.id); toast.success(`Joined ${data.title || "channel"}`); })
      .catch(() => toast.error("Invalid or expired invite link"))
      .finally(() => window.history.replaceState({}, "", "/app"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Deep-link to a shared product/post: /app?product=ID or /app?post=ID (also after guest sign-up)
  useEffect(() => {
    if (!user) return;
    const openFrom = (search) => {
      const params = new URLSearchParams(search);
      const prod = params.get("product");
      const post = params.get("post");
      if (prod) { setActiveId(null); setTab("market"); setMarketView("browse"); setMarketOpenId(prod); return true; }
      if (post) { setActiveId(null); setTab("social"); setSocialOpenPostId(post); return true; }
      return false;
    };
    let handled = openFrom(window.location.search);
    if (!handled) {
      try {
        const after = sessionStorage.getItem("rc_after_auth");
        if (after && after.includes("?")) handled = openFrom(after.split("?")[1]);
      } catch {}
    }
    try { sessionStorage.removeItem("rc_after_auth"); } catch {}
    if (handled) window.history.replaceState({}, "", "/app");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // In-app cross-platform navigation from shared cards (chat/social -> market/social)
  useEffect(() => {
    const onGoto = (e) => {
      const { kind, id } = e.detail || {};
      if (kind === "product") { setActiveId(null); setTab("market"); setMarketView("browse"); setMarketOpenId(id); }
      else if (kind === "post") { setActiveId(null); setTab("social"); setSocialOpenPostId(id); }
    };
    window.addEventListener("rc-goto", onGoto);
    return () => window.removeEventListener("rc-goto", onGoto);
  }, []);

  // Keep non-admins out of the (still-in-development) marketplace, no matter how
  // they land on it (deep-link, shared product card, payment return, live shop).
  useEffect(() => {
    if (tab === "market" && !canMarket) setTab("chats");
  }, [tab, canMarket]);

  // Greet returning users by name once per session
  useEffect(() => {
    if (user && !sessionStorage.getItem("rc_greeted")) {
      sessionStorage.setItem("rc_greeted", "1");
      const first = (user.name || "").split(" ")[0];
      toast(`${t("welcome_back")}${first ? `, ${first}` : ""} 👑`);
    }
  }, [user, t]);

  // App icon badge (installed PWA): reflect total unread messages on the launcher icon
  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;
    const total = chats.reduce((n, c) => n + (c.unread || 0), 0);
    if (total > 0) navigator.setAppBadge(total).catch(() => {});
    else if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
  }, [chats]);

  // Clear the badge when the user returns to the app in the foreground
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && "clearAppBadge" in navigator) {
        const total = chats.reduce((n, c) => n + (c.unread || 0), 0);
        if (total === 0) navigator.clearAppBadge().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [chats]);

  // Native (Capacitor) push registration — Android + iOS. No-op on the web.
  useEffect(() => {
    if (!user) return;
    let cleanup = () => {};
    import("@/lib/nativePush").then(({ isNativeApp, registerNativePush }) => {
      if (isNativeApp()) registerNativePush().then((fn) => { cleanup = fn; }).catch(() => {});
    });
    return () => cleanup();
  }, [user]);

  // Native push via Median (OneSignal) — ties the device to this user and handles
  // notification taps/deep-links. Inert on a normal browser (VAPID web push handles those).
  useEffect(() => {
    if (!user) return;
    let unsub = () => {};
    let cancelReady = () => {};
    import("@/lib/medianOneSignal").then(({ loginMedianOneSignal, subscribeMedianEvents, whenMedianReady }) => {
      const persist = (info) => {
        if (!info) return;
        api.post("/me/onesignal", {
          app_id: info.appId,
          one_signal_id: info.oneSignalId,
          external_id: info.externalId,
          subscription_id: info.subscription?.id,
          opted_in: info.subscription?.optedIn,
        }).catch(() => {});
      };
      cancelReady = whenMedianReady(async () => {
        unsub = subscribeMedianEvents({
          onInfo: persist,
          onOpened: () => { /* handled by the direct median:push-opened listener below */ },
        });
        const info = await loginMedianOneSignal(user.id).catch(() => null);
        persist(info);
      });
    });
    return () => { cancelReady(); unsub(); };
  }, [user]);

  // Web push via the standard Web Push API (VAPID) — for regular browsers + installed iOS/Android
  // PWAs. Never runs inside the Median native shell (guarded via getPushContext). Silently
  // refreshes the subscription (and heals stale iOS subs) when permission was already granted.
  useEffect(() => {
    if (!user) return;
    import("@/lib/push").then(({ refreshWebPush }) => { refreshWebPush(); }).catch(() => {});
  }, [user]);

  // Clear the notification tray + app badge whenever the app opens or comes to the foreground.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const clear = () => {
      if (cancelled) return;
      import("@/lib/medianOneSignal").then(({ clearAppNotifications }) => clearAppNotifications()).catch(() => {});
    };
    clear();
    const onVis = () => { if (document.visibilityState === "visible") clear(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", clear);
    return () => { cancelled = true; document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", clear); };
  }, [user]);

  // Web/PWA: the service worker posts a route intent when a notification is tapped while the
  // app is already open (native uses the Median push_opened callback above).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => { if (e.data && e.data.rcPushOpen && routeRef.current) routeRef.current(parsePushData(e.data)); };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []);

  // Prompt users without a phone (e.g. Google sign-ups) to complete their profile
  useEffect(() => {
    if (user && !user.phone && localStorage.getItem("rc_profile_prompt_dismissed") !== "1") {
      setShowProfilePrompt(true);
    } else {
      setShowProfilePrompt(false);
    }
  }, [user]);

  // Handle Stripe checkout return: /app?pay=success&session_id=... (poll until paid)
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const pay = params.get("pay");
    if (!pay) return;
    if (pay === "cancel") { toast("Payment cancelled"); window.history.replaceState({}, "", "/app"); setTab("market"); return; }
    if (pay === "success") {
      const sid = params.get("session_id");
      setTab("market");
      window.history.replaceState({}, "", "/app");
      if (!sid) return;
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            const msg = data.kind === "cart_order" ? "Order placed — thank you! 🎉"
              : data.kind === "boost" ? "Item boosted to the top! ⚡"
              : "Subscription active — you can sell now! 👑";
            toast.success(msg);
            return;
          }
          if (data.status === "expired" || tries > 8) { toast.error("Payment not completed"); return; }
        } catch (e) { if (tries > 8) return; }
        setTimeout(poll, 2000);
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Unread counts for Social & Market bottom-nav badges (with a light refresh)
  const refreshBadges = useCallback(() => {
    if (!user) return;
    Promise.all([
      api.get("/social/inbox").then(({ data }) => data.reduce((s, c) => s + (c.unread || 0), 0)).catch(() => 0),
      api.get("/social/notifications/count").then(({ data }) => data.count || 0).catch(() => 0),
    ]).then(([dm, notif]) => setSocialUnread(dm + notif));
    api.get("/market/inbox").then(({ data }) => setMarketUnread(data.reduce((s, c) => s + (c.unread || 0), 0))).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (!user) return;
    refreshBadges();
    const id = setInterval(refreshBadges, 30000);
    return () => clearInterval(id);
  }, [user, refreshBadges]);

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get("/chats");
      setChats(data);
      idb.saveChats(data);
    } catch (e) {
      const cached = await idb.getChats();
      if (cached.length) setChats(cached);
    }
  }, []);

  const loadMessages = useCallback(async (chatId) => {
    const cached = await idb.getMessages(chatId);
    if (cached.length) setMessages(cached.slice(-20));
    try {
      const { data } = await api.get(`/chats/${chatId}/messages`, { params: { limit: 20 } });
      // merge any still-pending outbox items for this chat
      const outbox = (await idb.getOutbox()).filter((o) => o.chat_id === chatId);
      setMessages([...data, ...outbox.map((o) => o.optimistic)]);
      idb.saveMessages(data);
      await api.post(`/chats/${chatId}/read`);
      loadChats();
    } catch (e) { /* offline: keep cached */ }
  }, [loadChats]);

  const flushOutbox = useCallback(async () => {
    const items = await idb.getOutbox();
    for (const it of items) {
      try {
        await api.post("/messages", it.payload);
        await idb.removeOutbox(it.temp_id);
      } catch (e) { break; } // stop on first failure, retry later
    }
    if (items.length && activeRef.current) loadMessages(activeRef.current);
  }, [loadMessages]);

  const sendMessage = useCallback(async (payload) => {
    if (navigator.onLine) {
      try { await api.post("/messages", payload); return; } catch (e) { /* fall through to queue */ }
    }
    // queue offline
    const temp_id = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const optimistic = {
      id: temp_id, temp_id, chat_id: payload.chat_id, sender_id: user.id, sender_name: user.name,
      type: payload.type, text: payload.text, media: payload.media, media_name: payload.media_name,
      reactions: {}, read_by: [user.id], deleted: false, pending: true, created_at: new Date().toISOString(),
    };
    await idb.addOutbox({ temp_id, chat_id: payload.chat_id, payload, optimistic });
    await idb.saveMessages([optimistic]);
    if (payload.chat_id === activeRef.current) setMessages((prev) => [...prev, optimistic]);
    toast("Saved — will send when you're back online", { icon: "📥" });
  }, [user.id, user.name]);

  // WebSocket realtime
  const onSocket = useCallback((msg) => {
    if (msg.type === "message") {
      const m = msg.message;
      idb.saveMessages([m]);
      if (m.sender_id !== user.id) playRecv();
      if (m.chat_id === activeRef.current) {
        setMessages((prev) => {
          const withoutTemp = prev.filter((x) => !(x.pending && x.type === m.type && x.text === m.text && x.sender_id === m.sender_id));
          return withoutTemp.some((x) => x.id === m.id) ? withoutTemp : [...withoutTemp, m];
        });
        if (m.sender_id !== user.id) api.post(`/chats/${m.chat_id}/read`);
      }
      loadChats();
    } else if (msg.type === "message_update") {
      const m = msg.message;
      setMessages((prev) => prev.map((x) => (x.id === m.id ? m : x)));
    } else if (msg.type === "typing") {
      setTyping((prev) => ({ ...prev, [msg.chat_id]: msg.is_typing ? msg.name : null }));
      if (msg.is_typing) setTimeout(() => setTyping((p) => ({ ...p, [msg.chat_id]: null })), 4000);
    } else if (msg.type === "read") {
      if (msg.chat_id === activeRef.current) {
        setMessages((prev) => prev.map((x) => (x.read_by && !x.read_by.includes(msg.reader_id) ? { ...x, read_by: [...x.read_by, msg.reader_id] } : x)));
      }
      loadChats();
    } else if (msg.type === "presence" || msg.type === "chat_update") {
      loadChats();
    } else if (msg.type === "chat_deleted") {
      if (msg.chat_id === activeRef.current) { setActiveId(null); setMessages([]); toast.info(t("channel_deleted_notice")); }
      loadChats();
    } else if (msg.type === "messages_expired") {
      if (msg.chat_id === activeRef.current) setMessages((prev) => prev.filter((x) => !(msg.ids || []).includes(x.id)));
      loadChats();
    } else if (msg.type === "broadcast") {
      toast(msg.text, { duration: 8000, icon: "📢" });
    } else if (msg.type === "new_post") {
      setNewPostSignal({ author_id: msg.author_id, ts: Date.now() });
    } else if (msg.type === "social_dm") {
      setSocialMsg(msg.message);
      const id = msg.message.id;
      if (msg.message.from !== user.id && !seenDmRef.current.has(id)) {
        seenDmRef.current.add(id);
        playRecv();
        if (tabRef.current !== "social") { setSocialUnread((n) => n + 1); toast(`💬 ${msg.message.from_name || "New"}: ${msg.message.text}`, { icon: "👥" }); }
      }
    } else if (msg.type === "market_dm") {
      setMarketMsg(msg.message);
      const id = msg.message.id;
      if (msg.message.from !== user.id && !seenDmRef.current.has(id)) {
        seenDmRef.current.add(id);
        playRecv();
        if (tabRef.current !== "market") { setMarketUnread((n) => n + 1); toast(`🛍️ ${msg.message.from_name || "Buyer"}: ${msg.message.text}`, { icon: "🛍️" }); }
      }
    } else if (msg.type === "social_friend_request") {
      toast(`${msg.from_name || "Someone"} sent you a friend request`, { icon: "👋" });
    } else if (msg.type === "wishlist_pricedrop") {
      toast(`💸 Price drop: ${msg.title} now ${msg.currency} ${msg.new_price}`, { icon: "💸", duration: 7000 });
    } else if (msg.type === "listing_expiring") {
      toast(`⏰ "${msg.title}" expires soon — repost to keep it live`, { icon: "⏰", duration: 7000 });
    } else if (msg.type === "social_friend_accepted") {
      toast(`${msg.from_name || "Someone"} accepted your friend request`, { icon: "🤝" });
    } else if (msg.type && msg.type.startsWith("rtc_")) {
      window.dispatchEvent(new CustomEvent("rc-rtc", { detail: msg }));
    } else if (msg.type === "live_now") {
      window.dispatchEvent(new CustomEvent("rc-live-now", { detail: msg }));
      toast(`🔴 ${msg.host_name} ${t("live_now")}`, {
        action: { label: t("s_watch"), onClick: () => window.dispatchEvent(new CustomEvent("rc-live-join", { detail: { session: { id: msg.live_id, host_name: msg.host_name, host_avatar: msg.host_avatar, module: msg.module, title: msg.title } } })) },
      });
    } else if (msg.type === "live_changed") {
      window.dispatchEvent(new CustomEvent("rc-live-now", { detail: msg }));
    } else if (msg.type === "scheduled_live_changed") {
      window.dispatchEvent(new CustomEvent("rc-scheduled-live", { detail: msg }));
    } else if (msg.type === "live_reminder") {
      window.dispatchEvent(new CustomEvent("rc-live-now", { detail: msg }));
      toast(`🔴 ${msg.host_name} ${t("live_now")}`, {
        action: { label: t("s_watch"), onClick: () => window.dispatchEvent(new CustomEvent("rc-live-join", { detail: { session: { id: msg.live_id, host_name: msg.host_name, module: "social", title: msg.title } } })) },
      });
    } else if (msg.type === "live_reminder_soon") {
      toast(`⏰ ${msg.host_name} — ${msg.title || t("live_soon_toast")}`, { icon: "⏰", duration: 7000 });
    } else if (msg.type && msg.type.startsWith("live_")) {
      window.dispatchEvent(new CustomEvent("rc-live", { detail: msg }));
    }
  }, [loadChats, user.id]);

  const { connected, send } = useSocket(onSocket);

  useEffect(() => { loadChats(); }, [loadChats]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  // flush outbox + poll catch-up whenever we (re)connect
  useEffect(() => { if (connected) flushOutbox(); }, [connected, flushOutbox]);

  // offline banner + poll catch-up
  useEffect(() => {
    const on = () => { setOnline(true); flushOutbox(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [flushOutbox]);

  // incoming call ringing (1:1 + group)
  useEffect(() => {
    const onRtc = (ev) => {
      const m = ev.detail;
      if (m.type === "rtc_offer" && !m.group) setIncomingCall((cur) => ({ from: m.from, from_name: m.from_name, sdp: m.sdp, video: m.video, autoAnswer: !!(cur && cur.autoAnswer) }));
      else if (m.type === "rtc_group_call") {
        setGroupCall((cur) => cur || { chatId: m.chat_id, video: m.video, incoming: true, from_name: m.from_name });
      } else if (m.type === "rtc_end" || m.type === "rtc_reject") setIncomingCall(null);
    };
    window.addEventListener("rc-rtc", onRtc);
    return () => window.removeEventListener("rc-rtc", onRtc);
  }, []);

  // Deep-link routing for push taps — used by BOTH the cold-start URL params AND the Median
  // push_opened callback (which fires even when the app is already loaded/backgrounded).
  const routePushOpen = useCallback((p) => {
    if (!p) return;
    try { localStorage.setItem("rc_last_push", JSON.stringify({ act: p.act, actRaw: p.actRaw, type: p.type, at: Date.now() })); } catch (e) { /* ignore */ }
    if (p.type === "call" && p.callerId) {
      setTab("chats"); setActiveId(null);
      if (p.act === "decline") {
        // Silently end for the caller — never reveal that the receiver declined.
        setPendingPushCall({ callerId: p.callerId, decline: true });
      } else {
        setIncomingCall({ from: p.callerId, from_name: p.callerName || "", video: !!p.video, fromPush: true, autoAnswer: p.act === "accept" });
        setPendingPushCall({ callerId: p.callerId });
      }
      return;
    }
    if (p.chatId) { setTab("chats"); setActiveId(p.chatId); }
  }, []);
  routeRef.current = routePushOpen;

  // Push-tap routing — resilient to Median's launch race and its targetUrl navigation:
  //   • a direct window listener catches `median:push-opened` whenever it fires (this carries
  //     Median's `actionId`, e.g. the Accept/Decline button the user pressed),
  //   • any tap buffered before mount (sessionStorage) is consumed on load,
  //   • cold-start deep-link URL params are the final fallback.
  // The Median native payload (with actionId) is preferred so tapping "Accept" auto-answers.
  useEffect(() => {
    if (!user) return;
    let done = false;
    const onOpened = (e) => { if (routeRef.current) routeRef.current(parsePushData(e.detail)); };
    window.addEventListener("median:push-opened", onOpened);
    (async () => {
      let intent = null;
      try {
        const { consumePushOpened } = await import("@/lib/medianOneSignal");
        const d = consumePushOpened && consumePushOpened();
        if (d) intent = parsePushData(d);
      } catch (e) { /* ignore */ }
      if (!intent) {
        const params = new URLSearchParams(window.location.search);
        const callerId = params.get("call");
        const chatId = params.get("chat");
        if (callerId) intent = { type: "call", callerId, callerName: params.get("name") || "", video: params.get("video") === "1", act: params.get("act") };
        else if (chatId) intent = { type: "message", chatId };
      }
      if (!done && intent) { window.history.replaceState({}, "", "/app"); if (routeRef.current) routeRef.current(intent); }
    })();
    return () => { done = true; window.removeEventListener("median:push-opened", onOpened); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Deterministic accept handshake for a call opened from a push. Runs whenever BOTH the socket
  // is live AND a pending push-call exists (order-independent — fixes the cold-start race where
  // the socket connected before/after the push intent was set). The accept signal (`rtc_ready`)
  // is RE-SENT a few times until the caller's re-offer (incomingCall.sdp) arrives, so a slow
  // cold-start or a missed first signal still connects. Decline ends the call silently.
  useEffect(() => {
    if (!connected || !pendingPushCall) return;
    const { callerId, decline } = pendingPushCall;
    if (decline) { send({ type: "rtc_end", to: callerId }); setIncomingCall(null); setPendingPushCall(null); return; }
    send({ type: "rtc_ready", to: callerId });
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (tries >= 6 || (incomingRef.current && incomingRef.current.sdp)) { clearInterval(iv); setPendingPushCall(null); return; }
      send({ type: "rtc_ready", to: callerId });
    }, 1500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, pendingPushCall]);

  // On (re)connect, ask the server whether a call is currently ringing for me. If so —
  // even when I didn't tap the push banner — jump straight to the incoming-call screen and
  // re-handshake the caller so the offer arrives and Accept works.
  useEffect(() => {
    if (!connected || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/calls/pending");
        const c = data && data.call;
        if (!cancelled && c && !incomingRef.current && !groupCall) {
          setTab("chats"); setActiveId(null);
          setIncomingCall({ from: c.caller_id, from_name: c.caller_name || "", video: !!c.video });
          setPendingPushCall({ callerId: c.caller_id });
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, user]);

  // Also re-check the ringing call whenever the app is brought back to the
  // foreground (tab focus / PWA resume), so reopening lands on the call screen.
  useEffect(() => {
    if (!user) return;
    const check = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const { data } = await api.get("/calls/pending");
        const c = data && data.call;
        if (c && !incomingRef.current && !groupCall) {
          setTab("chats"); setActiveId(null);
          setIncomingCall({ from: c.caller_id, from_name: c.caller_name || "", video: !!c.video });
          setPendingPushCall({ callerId: c.caller_id });
        }
      } catch (e) { /* ignore */ }
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => { window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", check); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, groupCall]);

  // Live streaming start/join triggers (dispatched by LiveBar in Social/Market)
  const [live, setLive] = useState(null); // { role, session }
  const [openProductId, setOpenProductId] = useState(null);
  useEffect(() => {
    const onStart = (ev) => setLive({ role: "host", session: ev.detail });
    const onJoin = (ev) => setLive({ role: "viewer", session: ev.detail.session });
    const onOpenProduct = (ev) => { setLive(null); setTab("market"); setOpenProductId(ev.detail.pid); };
    window.addEventListener("rc-live-start", onStart);
    window.addEventListener("rc-live-join", onJoin);
    window.addEventListener("rc-open-product", onOpenProduct);
    return () => { window.removeEventListener("rc-live-start", onStart); window.removeEventListener("rc-live-join", onJoin); window.removeEventListener("rc-open-product", onOpenProduct); };
  }, []);
  // Poll active lives so avatars show a red "live" ring across the app
  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/live/active").then(({ data }) => setLiveSessions(data)).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    const on = () => load();
    window.addEventListener("rc-live-now", on);
    return () => { clearInterval(id); window.removeEventListener("rc-live-now", on); };
  }, [user]);

  const activeChat = chats.find((c) => c.id === activeId) || null;

  const startChat = async (userId) => {
    const { data } = await api.post("/chats", { user_id: userId });
    setDialog(null);
    await loadChats();
    setActiveId(data.id);
  };

  const createGroup = async (name, memberIds) => {
    const { data } = await api.post("/groups", { name, member_ids: memberIds });
    setDialog(null);
    await loadChats();
    setActiveId(data.id);
  };

  const createChannel = async (payload) => {
    const { data } = await api.post("/channels", payload);
    setDialog(null);
    await loadChats();
    setActiveId(data.id);
  };

  const joinChannel = async (chatId) => {
    const { data } = await api.post(`/channels/${chatId}/join`);
    setDialog(null);
    await loadChats();
    setActiveId(data.id);
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-[var(--rc-bg)] text-[var(--rc-text)] flex flex-col">
      <AndroidUpdateBanner />
      <InstallCelebration />
      <IOSInstallPrompt />
      <NotificationPrompt />
      {(!connected || !online) && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-300 text-xs py-1.5 px-4 flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff size={13} /> {online ? "Reconnecting to RoyalChat Nova…" : "You're offline — messages will send when you're back"}
        </div>
      )}
      {showProfilePrompt && (
        <div className="bg-[var(--rc-gold)]/12 border-b border-[var(--rc-gold)]/30 text-[var(--rc-text)] text-sm py-2 px-4 flex items-center gap-3" data-testid="profile-prompt-banner">
          <span className="flex-1">{t("complete_profile_prompt")}</span>
          <button onClick={() => { setProfileOpen(true); setShowProfilePrompt(false); }} data-testid="profile-prompt-cta"
            className="shrink-0 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-3.5 py-1.5 text-xs">
            {t("complete_profile_cta")}
          </button>
          <button onClick={() => { localStorage.setItem("rc_profile_prompt_dismissed", "1"); setShowProfilePrompt(false); }} data-testid="profile-prompt-dismiss"
            className="shrink-0 text-[var(--rc-muted)] hover:text-[var(--rc-text)]" title={t("dismiss")}>
            <span className="text-lg leading-none">×</span>
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 relative">
        {/* Chats — two-pane on desktop, single view on mobile */}
        <div className={`h-full ${tab === "chats" ? "block md:block" : "hidden md:hidden"}`}>
          <div className="h-full grid md:grid-cols-[350px_1fr] min-w-0 overflow-hidden">
            <div className={`min-h-0 min-w-0 overflow-hidden border-r border-[var(--rc-border)] ${activeId ? "hidden md:block" : "block"}`}>
              <ChatList
                chats={chats}
                activeId={activeId}
                onSelect={setActiveId}
                onReload={loadChats}
                onNewChat={() => setDialog("new")}
                onOpenSettings={() => setDialog("settings")}
                onOpenAI={() => setDialog("ai")}
                onOpenAdmin={() => setDialog("admin")}
                typing={typing}
              />
            </div>
            <div className={`min-h-0 min-w-0 overflow-hidden ${activeId ? "block" : "hidden md:block"}`}>
              <ChatThread
                chat={activeChat}
                messages={messages}
                setMessages={setMessages}
                onBack={() => setActiveId(null)}
                sendWs={send}
                sendMessage={sendMessage}
                typingName={activeId ? typing[activeId] : null}
                onReload={loadChats}
                autoCall={pendingCall && activeChat && pendingCall.chatId === activeChat.id ? pendingCall : null}
                onAutoCallConsumed={() => setPendingCall(null)}
                onStartGroupCall={(video) => setGroupCall({ chatId: activeChat.id, video, incoming: false })}
              />
            </div>
          </div>
        </div>

        {/* Social tab */}
        {tab === "social" && (
          <div className="absolute inset-0">
            <SocialPage socialMsg={socialMsg} newPostSignal={newPostSignal} openPostId={socialOpenPostId} onPostOpened={() => setSocialOpenPostId(null)} onImmersiveChange={setSocialImmersive}
              onLeaveSocial={async () => {
                try {
                  const { data } = await api.post("/social/opt-out");
                  setUser(data);
                  setSocialImmersive(false); setActiveId(null); setTab("chats");
                } catch (e) { toast.error(formatApiError(e)); }
              }} />
          </div>
        )}

        {/* Marketplace tab — admin only for now */}
        {tab === "market" && canMarket && (
          <div className="absolute inset-0">
            <MarketplacePage socialMsg={marketMsg} initialView={marketView} openProductId={marketOpenId} onProductOpened={() => setMarketOpenId(null)} />
          </div>
        )}

        {/* Calls tab */}
        {tab === "calls" && (
          <div className="absolute inset-0">
            <CallsPage onCall={(c) => {
              const chatId = c.chatId || chats.find((ch) => ch.type === "direct" && ch.peer?.id === c.peerId)?.id;
              if (chatId) { setTab("chats"); setActiveId(chatId); setPendingCall({ chatId, video: !!c.video }); }
            }} />
          </div>
        )}

        {/* Settings tab */}
        {tab === "settings" && (
          <div className="absolute inset-0">
            <SettingsPage
              connected={connected && online}
              onOpenProfile={() => setProfileOpen(true)}
              onOpenAI={() => setDialog("ai")}
              onOpenAdmin={() => setDialog("admin")}
              onOpenMarket={() => { setMarketView("shop"); setTab("market"); }}
            />
          </div>
        )}
      </div>

      <div className={`transition-opacity duration-300 ${tab === "social" && socialImmersive ? "opacity-30" : "opacity-100"} ${activeId && tab === "chats" ? "hidden md:block" : "block"}`}>
        <BottomNav tab={tab} badges={{ social: socialUnread }} setTab={(v) => {
          // New users must opt in before the Social tab unlocks. Existing users are already enabled.
          if (v === "social" && !user?.social_enabled) { setSocialJoinOpen(true); return; }
          setSocialImmersive(false); setTab(v); if (v !== "chats") setActiveId(null);
          if (v === "social") { setSocialUnread(0); api.post("/social/notifications/seen").then(() => refreshBadges()).catch(() => {}); }
        }} />
      </div>

      <SocialConsentDialog open={socialJoinOpen} variant="join" busy={socialOptBusy}
        onCancel={() => setSocialJoinOpen(false)}
        onConfirm={async () => {
          setSocialOptBusy(true);
          try {
            const { data } = await api.post("/social/opt-in");
            setUser(data);
            setSocialJoinOpen(false);
            setSocialImmersive(false); setActiveId(null); setTab("social");
            setSocialUnread(0);
            api.post("/social/notifications/seen").then(() => refreshBadges()).catch(() => {});
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setSocialOptBusy(false); }
        }} />

      {profileOpen && <ProfileSheet onClose={() => setProfileOpen(false)} />}
      {dialog === "new" && <NewChatDialog onClose={() => setDialog(null)} onStartChat={startChat} onCreateGroup={createGroup} onCreateChannel={createChannel} onJoinChannel={joinChannel} />}
      {dialog === "settings" && <SettingsDialog onClose={() => setDialog(null)} />}
      {dialog === "ai" && <AIDialog onClose={() => setDialog(null)} />}
      {dialog === "admin" && <AdminDialog onClose={() => setDialog(null)} />}

      {incomingCall && (
        <CallOverlay incoming={incomingCall} video={incomingCall.video} sendWs={send} onClose={() => setIncomingCall(null)} />
      )}
      {groupCall && (
        <GroupCallOverlay
          chat={chats.find((c) => c.id === groupCall.chatId)}
          video={groupCall.video}
          incoming={groupCall.incoming}
          fromName={groupCall.from_name}
          sendWs={send}
          onClose={() => setGroupCall(null)}
        />
      )}
      {locked && <LockScreen onUnlock={() => setLocked(false)} />}
      {live && <LiveScreen role={live.role} session={live.session} sendWs={send} onClose={() => setLive(null)} />}
    </div>
  );
}
